import { getDatabase, ref, get, set, update, push } from 'firebase/database';
import { RoomType } from '../types/Enums';
import { BlueprintLocation, ValueStatus } from '../types/protocols';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';

const ROOM_TYPES = Object.values(RoomType) as string[];

/** Fields of a room created from the parking area form. */
export interface RoomCreateInput {
    levelKey: string;
    type?: RoomType;
    number?: string;
    label?: string;
}

/**
 * Room node written by createRoom. Same shape as iOS RoomWriter: iOS decodes
 * `ceilingHeight` and `areaShapes` as non-optional, so both are always written
 * as empty ValueWithMetadata (`{ status: 'notSet' }`); a missing key would fail
 * decoding of the building's whole `structure/rooms` map.
 */
export interface CreatedRoomNode {
    key: string;
    levelKey: string;
    type?: RoomType;
    number?: string;
    label?: string;
    ceilingHeight: { status: ValueStatus.notSet };
    areaShapes: { status: ValueStatus.notSet };
}

const trimmedOrUndefined = (value: string | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
};

/** Rooms at `buildings/{buildingKey}/structure/rooms/{roomKey}`. */
export class RoomsApi {
    private db = getDatabase();

    getRoomPath(buildingKey: string, roomKey: string): string {
        return `buildings/${buildingKey}/structure/rooms/${roomKey}`;
    }

    /**
     * Creates a room on a level and returns its key. Admin only, like
     * creating a parking area. Placing the room on the drawing is done later in
     * the inventory.
     */
    async createRoom(buildingKey: string, input: RoomCreateInput): Promise<string> {
        await requireAdmin(this.db, 'skapa rum');

        const levelKey = input.levelKey?.trim();
        if (!levelKey) {
            throw new Error('Våning krävs för att skapa ett rum');
        }
        if (input.type !== undefined && !ROOM_TYPES.includes(input.type)) {
            throw new Error('Ogiltig rumstyp');
        }
        const levelSnapshot = await get(ref(this.db, `buildings/${buildingKey}/structure/levels/${levelKey}`));
        if (!levelSnapshot.exists()) {
            throw new Error('Våningen finns inte längre');
        }

        const newRef = push(ref(this.db, `buildings/${buildingKey}/structure/rooms`));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för rummet');
        }

        const node: CreatedRoomNode = {
            key,
            levelKey,
            ceilingHeight: { status: ValueStatus.notSet },
            areaShapes: { status: ValueStatus.notSet },
        };
        if (input.type) node.type = input.type;
        const number = trimmedOrUndefined(input.number);
        if (number) node.number = number;
        const label = trimmedOrUndefined(input.label);
        if (label) node.label = label;

        // Brand-new node: set() is safe, nothing nested exists yet.
        await set(ref(this.db, this.getRoomPath(buildingKey, key)), node);
        recordActivity('room.created', { buildingKey, targetKey: key, label: label ?? number });
        return key;
    }

    /**
     * Sets or clears (location = null) the room's marker on one level drawing,
     * keyed by that drawing's image key like every other marker. Used when a
     * single-room unit's pin is dragged (units plan D5).
     */
    async setRoomLocation(buildingKey: string, roomKey: string, imageKey: string, location: BlueprintLocation | null): Promise<void> {
        await requireAdmin(this.db, 'placera rum');
        await update(ref(this.db, this.getRoomPath(buildingKey, roomKey)), {
            [`blueprintLocations/${imageKey}`]: location ? { ...location, key: imageKey } : null,
        });
        recordActivity('room.updated', { buildingKey, targetKey: roomKey });
    }
}

export const roomsApi = new RoomsApi();
